import { useRef, useState } from 'react';
import { message } from 'antd';
import {
  usePinSendMutation,
  usePinResendMutation,
  usePinSignMutation,
} from 'features/card/get-pin/api';
import { CodeInputRef } from 'shared/ui/CodeInput/CodeInput';
import { useIntl } from 'react-intl';

export const useGetPin = (accountId: string, cardId: number) => {
  const { formatMessage: t } = useIntl();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isSubmittingCode, setIsSubmittingCode] = useState(false);
  const [pin, setPin] = useState<string | null>(null);

  const codeInputRef = useRef<CodeInputRef>(null);

  const [pinSend] = usePinSendMutation();
  const [pinResend] = usePinResendMutation();
  const [pinSign] = usePinSignMutation();

  const handleGetPinClick = async () => {
    try {
      await pinSend({ accountId: Number(accountId), cardId: cardId }).unwrap();
      setDrawerVisible(true);
      setPin(null);
      setTimeout(() => {
        codeInputRef.current?.focus();
      }, 300);
    } catch {
      message.error(t({ id: 'common.otp.error' }));
    }
  };

  const handleCodeSubmit = async (code: string) => {
    setIsSubmittingCode(true);
    try {
      const result = await pinSign({ accountId: Number(accountId), cardId: cardId, code }).unwrap();

      if (result?.response?.pin) {
        codeInputRef.current?.success();
        setPin(result.response.pin);
      } else {
        codeInputRef.current?.error(t({ id: 'common.message.codeInvalid' }));
      }
    } catch {
      codeInputRef.current?.error(t({ id: 'common.message.codeInvalid' }));
    } finally {
      setIsSubmittingCode(false);
    }
  };

  const handleCodeResend = async () => {
    try {
      await pinResend({ accountId: Number(accountId), cardId: cardId }).unwrap();
      message.success(t({ id: 'common.otp.resend' }));
    } catch {
      message.error(t({ id: 'common.otp.error' }));
    }
  };

  return {
    drawerVisible,
    setDrawerVisible,
    codeInputRef,
    handleGetPinClick,
    handleCodeSubmit,
    handleCodeResend,
    isSubmittingCode,
    pin,
  };
};
